import { useEffect } from 'react';
import { Icon } from '../../ui';
import { useT } from '../../i18n/i18n.tsx';
import { CODE_DIGITS, applyCodeKey, keyFromKeyboardEvent, type CodeKey } from './guestCode';
import { isTypingTarget } from '../../lib/isTypingTarget.ts';

/**
 * MVP-274 R1 — the guest page's primary way in: the code printed next to every song in the venue's
 * book, entered on a pad big enough to hit without looking. See `guestCode.ts` for why this door
 * exists at all; this file only draws it.
 *
 * Deliberately NOT an `<input inputMode="numeric">`. That would summon the OS keyboard, which on a
 * phone covers the bottom half of the screen — including the results this pad is meant to produce —
 * and whose key size is decided by the OS, not by DESIGN.md §6.3's 44px guest floor. An on-screen
 * pad keeps the entered code, the keys and the first results visible at once, which is the whole
 * interaction: type a number, see the song, tap it.
 */
export function GuestCodePad({ code, onCodeChange }: { code: string; onCodeChange: (next: string) => void }) {
  const t = useT();

  // A laptop or a tablet with a keyboard is a real way this page gets opened (a KJ checking what the
  // room sees; a guest who has one). Typing a digit there must reach the pad rather than fall on the
  // floor — see `keyFromKeyboardEvent`. Bound to the document, not to a focused element: nothing on
  // this pad holds focus after a tap, so there is no element for a key event to arrive at.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      // Never steal keystrokes from a real field — the passcode gate and the request sheet's name
      // field both mount on this same page. MVP-496: the shared predicate, so this pad and the
      // operator hotkeys can never disagree about what counts as a field again.
      if (isTypingTarget(target)) return;
      // Nor from behind the request sheet. It opens over this pad, and a digit typed while it is up
      // (focus sitting on the panel rather than in its name field) would silently rewrite the list
      // the customer comes back to after requesting a song.
      if (document.querySelector('[role="dialog"]')) return;
      const key = keyFromKeyboardEvent(e.key);
      if (!key) return;
      e.preventDefault();
      onCodeChange(applyCodeKey(code, key));
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [code, onCodeChange]);

  function press(key: CodeKey) {
    onCodeChange(applyCodeKey(code, key));
  }

  return (
    <div className="guest-codepad">
      {/* `aria-live` so a screen-reader user hears the number they just built, not only the key they
          pressed — the digits are the state, and nothing else on screen announces them. */}
      <div className="guest-codepad__display" role="status" aria-live="polite" aria-label={t('guest.code.enteredLabel')}>
        {code ? (
          <span className="guest-codepad__digits text-display tabular-nums">{code}</span>
        ) : (
          <span className="guest-codepad__hint text-body">{t('guest.code.hint')}</span>
        )}
      </div>

      <div className="guest-codepad__keys">
        {CODE_DIGITS.map((digit) => (
          <button key={digit} type="button" className="guest-codepad__key text-title tabular-nums" onClick={() => press(digit)}>
            {digit}
          </button>
        ))}
        <button
          type="button"
          className="guest-codepad__key guest-codepad__key--action"
          onClick={() => press('clear')}
          disabled={code.length === 0}
          aria-label={t('guest.code.clear')}
        >
          <Icon name="close" size={22} />
        </button>
        <button key="0" type="button" className="guest-codepad__key text-title tabular-nums" onClick={() => press('0')}>
          0
        </button>
        <button
          type="button"
          className="guest-codepad__key guest-codepad__key--action"
          onClick={() => press('back')}
          disabled={code.length === 0}
          aria-label={t('guest.code.backspace')}
        >
          <Icon name="chevron-left" size={22} />
        </button>
      </div>
    </div>
  );
}
